import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { CardComponent } from './components/CardComponent.jsx';
import { AdditionallyCardComponent } from './components/AdditionallyCardComponent.jsx';
import HeaderMainPage from './HeaderMainPage/HeaderMainPage.jsx';
import Footer from './FooterMainPage/FooterMainPage.jsx';
import ManagerLayout from './manager-layout/ManagerLayout.tsx';
import { InformationCardComponent } from './components/InformationCardComponent.jsx';
import { additionallyCards, informationCard } from './Cards/CardsContent.js';
import { ClientTypes } from '~/constants/clientTypes.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { infoMessages } from '~/constants/infoMessages.js';
import { CardsLegalResident } from '~/modules/main-page/components/CardsLegalResident.jsx';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { styles } from '~/modules/main-page/styles.ts';
import { useMainCardsData } from './hooks/useMainCardsData.ts';
import {
  handleRate,
  openCRS,
  openDirectoryOrgUa,
  openFatca,
  openInstructionLegalResident,
} from './utils.ts';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { IdentificationType } from '../identification/forms/GettingStarted/types.ts';
import { clearForm } from '~/store/identification-form/slices.ts';
import { selectHasGuaranteesManagerAccess } from '~/store/guarantees/selectors.ts';

const MainPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(() => {
    const savedStep = sessionStorage.getItem('mainPageActiveStep');
    return savedStep || 'LEGAL_RESIDENT';
  });
  const [isModalAlertOpen, setIsModalAlertOpen] = useState(false);
  const [modalAlertText, setModalAlertText] = useState('');

  const { isManager, isClientLogin, isManagerLogin, isSupervisor } = useGetUserRole();
  const hasGuaranteesManagerAccess = useSelector(selectHasGuaranteesManagerAccess);
  const canShowGuarantees = !isManagerLogin || hasGuaranteesManagerAccess;
  const handleOpenStep = (buttonName: string) => setActiveStep(buttonName);
  const handleCloseAlertModal = () => setIsModalAlertOpen(false);

  useEffect(() => {
    dispatch(clearForm());
  }, []);

  useEffect(() => {
    sessionStorage.setItem('mainPageActiveStep', activeStep);
  }, [activeStep]);

  const handleCheckUserRegistration = () => {
    const redirectPath = `/identification/questionnaire?type=${IdentificationType.PRIMARY}`;

    if (isManagerLogin && !isManager && !isSupervisor) {
      setIsModalAlertOpen(true);
      setModalAlertText(infoMessages.LIMITED_ACCESS_TO_FUNCTIONALITY);
      return;
    }

    if (isClientLogin || (isManagerLogin && (isManager || isSupervisor))) {
      navigate(redirectPath);
      return;
    }

    navigate('/sign-in', {
      state: { from: redirectPath },
    });
  };

  const handleReidentification = () => {
    const redirectPath = `/identification/questionnaire?type=${IdentificationType.RE_IDENTIFICATION}`;

    if (isManagerLogin && !isManager && !isSupervisor) {
      setIsModalAlertOpen(true);
      setModalAlertText(infoMessages.LIMITED_ACCESS_TO_FUNCTIONALITY);
      return;
    }

    if (isClientLogin || (isManagerLogin && (isManager || isSupervisor))) {
      navigate(redirectPath);
      return;
    }

    navigate('/sign-in', {
      state: { from: redirectPath },
    });
  };

  const handleUpdatingData = (bannerType: string) => {
    const redirectPath =
      bannerType === ClientTypes.SOLE_TRADER
        ? '/data-update?sole-trader'
        : '/data-update?legal-resident';

    if (isClientLogin || (isManagerLogin && isManager)) {
      navigate(redirectPath);
      return;
    }

    navigate('/sign-in', {
      state: { from: redirectPath },
    });
  };

  const handleOpenCovenants = () => {
    if (isClientLogin) {
      navigate('/covenants/get-started');
      return;
    }

    if (isManagerLogin) {
      setIsModalAlertOpen(true);
      setModalAlertText(infoMessages.LIMITED_ACCESS_TO_FUNCTIONALITY);
      return;
    }

    navigate('/sign-in', {
      state: { from: '/covenants/get-started' },
    });
  };

  const handleOpenGuarantees = () => {
    if (isClientLogin) {
      navigate('/guarantees/get-started');
      return;
    }

    if (isManagerLogin) {
      navigate('/guarantees/administrator/get-started');
      return;
    }

    navigate('/sign-in', {
      state: { from: '/guarantees/get-started' },
    });
  };

  const mainCardsData = useMainCardsData({
    activeStep,
    handleCheckUserRegistration,
    handleOpenCovenants,
    handleOpenGuarantees,
    handleUpdatingData,
    handleReidentification,
    canShowGuarantees,
  });

  const pageContent = (
    <Box sx={styles.mainPage}>
      <Box sx={styles.mainPageContainer}>
        {!isManagerLogin && <HeaderMainPage />}
        <Box sx={styles.mainPageContent}>
          <Grid>
            <Box sx={styles.mainPageNavigator}>
              <Button
                onClick={() => handleOpenStep('LEGAL_RESIDENT')}
                sx={(theme) => ({
                  ...styles.navigatorButton(theme),
                  ...(activeStep === 'LEGAL_RESIDENT' && {
                    color: theme.palette.primary.main,
                  }),
                })}
              >
                Юридичним особам
              </Button>
              <Button
                onClick={() => handleOpenStep('SOLE_TRADER')}
                sx={(theme) => ({
                  ...styles.navigatorButton(theme),
                  ...(activeStep === 'SOLE_TRADER' && {
                    color: theme.palette.primary.main,
                  }),
                })}
                data-testid="sole-trader-button"
              >
                ФОП
              </Button>
              <Button
                onClick={() => handleOpenStep('COUNTERPARTY')}
                sx={(theme) => ({
                  ...styles.navigatorButton(theme),
                  ...(activeStep === 'COUNTERPARTY' && {
                    color: theme.palette.primary.main,
                  }),
                })}
              >
                Контрагентам
              </Button>
            </Box>
            <Typography sx={styles.mainPageContentNavigator}>
              Сервіси і послуги, які можна отримати online, не відвідуючи відділень банку
            </Typography>
          </Grid>
          <Box sx={styles.mainPageContentWrapper}>
            {activeStep === 'LEGAL_RESIDENT' ? (
              <CardsLegalResident
                handleCheckUserRegistration={handleCheckUserRegistration}
                handleRate={handleRate}
                openInstructionLegalResident={openInstructionLegalResident}
              />
            ) : null}
          </Box>
          <Box sx={styles.mainPageContentWrapper}>
            {activeStep === 'LEGAL_RESIDENT' ? (
              <InformationCardComponent cards={informationCard as []} />
            ) : (
              ''
            )}
          </Box>
          <Box sx={styles.mainPageContentWrapper}>
            {activeStep !== 'LEGAL_RESIDENT' ? <CardComponent cards={mainCardsData as []} /> : ''}
          </Box>
          <Box sx={styles.mainPageContentWrapper}>
            {activeStep !== 'LEGAL_RESIDENT' ? null : <CardComponent cards={mainCardsData as []} />}
          </Box>
          <Box sx={styles.mainPageContentCardWrapper}>
            <Grid sx={styles.mainPageContentCardText}>
              <Typography variant={'h5'} sx={styles.mainPageContentCardTextInfo}>
                Додаткова інформація:
              </Typography>
              <hr />
            </Grid>
            <AdditionallyCardComponent
              cards={additionallyCards(openDirectoryOrgUa, openFatca, openCRS) as []}
            />
          </Box>
        </Box>
        <Footer />
      </Box>
      {isModalAlertOpen && (
        <ModalAlert
          isOpen={isModalAlertOpen}
          text={modalAlertText}
          onClose={handleCloseAlertModal}
        />
      )}
    </Box>
  );

  return isManagerLogin ? <ManagerLayout>{pageContent}</ManagerLayout> : pageContent;
};

export default MainPage;
